/**
 * @file Route-local optimistic Calendar entry helpers.
 */

import {
    removeCalendarSourceEntry,
    removeOptimisticCalendarEntry,
    replaceOptimisticCalendarEntry,
    upsertOptimisticCalendarEntry,
} from "./calendarState.js";
import { state } from "../../app/state/state.js";

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function getTemporarySourceId(type) {
    return `optimistic-${type}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getCurrentUserId() {
    return state.currentUser?.uid ||
        state.currentUserProfile?.id ||
        state.currentUserProfile?.uid ||
        "";
}

function normalizeUserIds(userIds = []) {
    return [...new Set((Array.isArray(userIds) ? userIds : [])
        .map((userId) => String(userId || "").trim())
        .filter(Boolean))]
        .sort((a, b) => a.localeCompare(b));
}

function parseDateString(dateString) {
    if (!DATE_PATTERN.test(String(dateString || ""))) {return null;}
    const parsed = new Date(`${dateString}T00:00:00.000Z`);
    if (Number.isNaN(parsed.getTime())) {return null;}
    return parsed.toISOString().slice(0, 10) === dateString ? parsed : null;
}

function calculateMonthKeys(startDate, endDate) {
    const start = parseDateString(startDate);
    const end = parseDateString(endDate);
    if (!start || !end || end.getTime() < start.getTime()) {return [];}
    const cursor = new Date(Date.UTC(
        start.getUTCFullYear(),
        start.getUTCMonth(),
        1,
    ));
    const endCursor = new Date(Date.UTC(
        end.getUTCFullYear(),
        end.getUTCMonth(),
        1,
    ));
    const monthKeys = [];
    while (cursor.getTime() <= endCursor.getTime()) {
        monthKeys.push(cursor.toISOString().slice(0, 7));
        cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    }
    return monthKeys;
}

function buildVisibilityMonthKeys(visibleTo = [], monthKeys = []) {
    const keys = [];
    visibleTo.forEach((userId) => {
        monthKeys.forEach((monthKey) => keys.push(`${userId}:${monthKey}`));
    });
    return keys;
}

function refreshCalendarView() {
    if (!document.getElementById("calendar-board")) {return;}
    import("./calendarView.js")
        .then(({ renderCalendarView }) => renderCalendarView())
        .catch((error) => {
            console.error("Failed to refresh optimistic Calendar view:", error);
        });
}

function buildEventEntry(eventData = {}, sourceId = null) {
    const visibleTo = normalizeUserIds(
        Array.isArray(eventData.visibilityUserIds) ?
            eventData.visibilityUserIds :
            [
                ...(Array.isArray(eventData.hostIds) ? eventData.hostIds : []),
                ...(Array.isArray(eventData.attendeeIds) ? eventData.attendeeIds : []),
                getCurrentUserId(),
            ],
    );
    const monthKeys = calculateMonthKeys(
        eventData.startDate,
        eventData.endDate || eventData.startDate,
    );
    return {
        sourceType: "event",
        sourceId: sourceId || eventData.id || getTemporarySourceId("event"),
        title: eventData.title || "Untitled event",
        startDate: eventData.startDate || null,
        endDate: eventData.endDate || eventData.startDate || null,
        startTime: eventData.startTime || null,
        endTime: eventData.endTime || null,
        isFullDay: eventData.isFullDay === true,
        monthKeys,
        visibleTo,
        visibilityMonthKeys: buildVisibilityMonthKeys(visibleTo, monthKeys),
        renderKind: "event",
        visibility: "calendar",
    };
}

function buildReminderEntry(reminderData = {}, sourceId = null) {
    const visibleTo = normalizeUserIds([
        reminderData.creatorId || getCurrentUserId(),
        ...(Array.isArray(reminderData.assignedUserIds) ?
            reminderData.assignedUserIds :
            []),
    ]);
    const monthKeys = calculateMonthKeys(
        reminderData.reminderDate || reminderData.startDate,
        reminderData.reminderDate || reminderData.startDate,
    );
    return {
        sourceType: "reminder",
        sourceId: sourceId || reminderData.id || getTemporarySourceId("reminder"),
        title: reminderData.title || "Untitled reminder",
        startDate: reminderData.reminderDate || reminderData.startDate || null,
        endDate: reminderData.reminderDate || reminderData.startDate || null,
        startTime: null,
        endTime: null,
        isFullDay: false,
        monthKeys,
        visibleTo,
        visibilityMonthKeys: buildVisibilityMonthKeys(visibleTo, monthKeys),
        renderKind: "reminder",
        visibility: "calendar",
    };
}

/**
 * Adds an optimistic Event entry and refreshes the Calendar route.
 * @param {object} eventData Event payload.
 * @param {string|null} [sourceId=null] Source id.
 * @returns {string} Optimistic id.
 */
export function addOptimisticEventEntry(eventData, sourceId = null) {
    const optimisticId = upsertOptimisticCalendarEntry(
        buildEventEntry(eventData, sourceId),
    );
    refreshCalendarView();
    return optimisticId;
}

/**
 * Reconciles an optimistic Event entry with callable response data.
 * @param {string} optimisticId Optimistic id.
 * @param {object} response Callable response.
 * @returns {void}
 */
export function reconcileOptimisticEventEntry(optimisticId, response = {}) {
    const eventData = response.event || response.data || {};
    const eventId = response.eventId || eventData.id || null;
    if (!eventId) {return;}
    replaceOptimisticCalendarEntry(
        optimisticId,
        buildEventEntry(eventData, eventId),
    );
    refreshCalendarView();
}

/**
 * Adds an optimistic Reminder entry and refreshes the Calendar route.
 * @param {object} reminderData Reminder payload.
 * @param {string|null} [sourceId=null] Source id.
 * @returns {string} Optimistic id.
 */
export function addOptimisticReminderEntry(reminderData, sourceId = null) {
    const optimisticId = upsertOptimisticCalendarEntry(
        buildReminderEntry(reminderData, sourceId),
    );
    refreshCalendarView();
    return optimisticId;
}

/**
 * Reconciles an optimistic Reminder entry with callable response data.
 * @param {string} optimisticId Optimistic id.
 * @param {object} response Callable response.
 * @returns {void}
 */
export function reconcileOptimisticReminderEntry(optimisticId, response = {}) {
    const reminderData = response.reminder || response.data || {};
    const reminderId = response.reminderId || reminderData.id || null;
    if (!reminderId) {return;}
    replaceOptimisticCalendarEntry(
        optimisticId,
        buildReminderEntry(reminderData, reminderId),
    );
    refreshCalendarView();
}

/**
 * Removes a failed optimistic entry and refreshes the Calendar route.
 * @param {string} optimisticId Optimistic id.
 * @returns {void}
 */
export function discardOptimisticEntry(optimisticId) {
    removeOptimisticCalendarEntry(optimisticId);
    refreshCalendarView();
}

/**
 * Removes a definitive Calendar source entry from the route-local cache.
 * @param {string} sourceType Calendar source type.
 * @param {string} sourceId Source document id.
 * @returns {void}
 */
export function removeCalendarEntry(sourceType, sourceId) {
    removeCalendarSourceEntry(sourceType, sourceId);
    refreshCalendarView();
}
